// Shared CSS for the public website (landing / privacy / delete-account).
// Ported from admin/src/site/siteStyles.js so both surfaces stay identical.
// Injected as a <style> tag: real media queries + keyframes, which inline
// styles can't express. Theme follows the Narayan logo (brand red).
const siteCss = `
.site{
  --brand:#C1121F; --brand-dark:#9A0E18; --brand-light:#E63946; --brand-soft:#FDECEE;
  --accent:#E8A33D; --ink:#1B1D28; --muted:#6B6F80; --faint:#9AA0B0;
  --bg:#FFFFFF; --bg-alt:#F7F8FC; --line:#ECEDF2;
}
.site *{box-sizing:border-box}
.site{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;color:var(--ink);background:var(--bg);overflow-x:hidden;min-height:100vh}
.site img{max-width:100%}
.site .wrap{max-width:1140px;margin:0 auto;padding:0 20px}

/* Scroll-reveal (IntersectionObserver driven — no animation library). */
.site .reveal{opacity:0;transform:translateY(24px);transition:opacity .5s cubic-bezier(.22,1,.36,1),transform .5s cubic-bezier(.22,1,.36,1)}
.site .reveal.in{opacity:1;transform:none}

/* Nav */
.site .nav{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.92);backdrop-filter:blur(10px);border-bottom:1px solid var(--line)}
.site .nav-in{display:flex;align-items:center;justify-content:space-between;height:68px;gap:16px}
.site .nav-logo{height:38px;width:auto}
.site .nav-links{display:flex;align-items:center;gap:28px}
.site .nav-links a{color:var(--muted);text-decoration:none;font-size:14.5px;font-weight:600}
.site .nav-links a:hover{color:var(--brand)}
.site .nav-actions{display:flex;align-items:center;gap:10px;flex:none}
/* width/min-height reset the app shell's global .btn, which is a full-width
   mobile button — site buttons must hug their label. */
.site .btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;border:none;cursor:pointer;text-decoration:none;font-weight:800;border-radius:999px;transition:transform .15s ease, box-shadow .15s ease;font-family:inherit;white-space:nowrap;width:auto;min-height:0}
.site .btn:hover{transform:translateY(-2px)}
.site .btn-primary{background:var(--brand);color:#fff;padding:12px 22px;font-size:14.5px;box-shadow:0 8px 22px rgba(193,18,31,.32)}
.site .btn-ghost{background:#fff;color:var(--brand);border:1.5px solid var(--brand);padding:12px 22px;font-size:14.5px}
.site .btn-lg{padding:15px 30px;font-size:16px}

/* Hero */
.site .hero{position:relative;background:linear-gradient(135deg,var(--brand-dark) 0%,var(--brand) 55%,var(--brand-light) 100%);color:#fff;overflow:hidden}
.site .hero:before,.site .hero:after{content:'';position:absolute;border-radius:999px;filter:blur(2px);opacity:.18;background:#fff}
.site .hero:before{width:420px;height:420px;top:-160px;right:-120px}
.site .hero:after{width:300px;height:300px;bottom:-150px;left:-90px;opacity:.12}
.site .hero-in{position:relative;display:grid;grid-template-columns:1.1fr .9fr;gap:40px;align-items:center;padding:84px 20px 92px}
.site .hero h1{font-size:52px;line-height:1.08;margin:0 0 18px;font-weight:900;letter-spacing:-.5px}
.site .hero p{font-size:17.5px;line-height:1.6;opacity:.92;margin:0 0 28px;max-width:520px}
.site .hero-cta{display:flex;gap:14px;flex-wrap:wrap}
.site .hero .btn-ghost{background:transparent;color:#fff;border-color:rgba(255,255,255,.6)}
.site .hero-badges{display:flex;gap:10px;flex-wrap:wrap;margin-top:26px}
.site .pill{background:rgba(255,255,255,.16);border:1px solid rgba(255,255,255,.28);border-radius:999px;padding:7px 14px;font-size:12.5px;font-weight:600}
.site .hero-anim{animation:site-rise .6s cubic-bezier(.22,1,.36,1) both}
.site .hero-anim-2{animation:site-pop .6s .15s cubic-bezier(.22,1,.36,1) both}
@keyframes site-rise{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
@keyframes site-pop{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:none}}

/* Banner carousel */
.site .banner-box{border-radius:22px;overflow:hidden;box-shadow:0 24px 60px rgba(0,0,0,.28);background:rgba(255,255,255,.1);aspect-ratio:2.5/1;position:relative}
.site .banner-box img{width:100%;height:100%;object-fit:cover;display:block}
.site .banner-dots{position:absolute;bottom:12px;left:0;right:0;display:flex;justify-content:center;gap:6px}
.site .banner-dots i{width:7px;height:7px;border-radius:999px;background:rgba(255,255,255,.5)}
.site .banner-dots i.on{background:#fff;width:18px}
.site .hero-fallback{display:flex;align-items:center;justify-content:center;font-size:88px;height:100%}

/* Hero banner carousel — a real scroll-snap track so touch swipe and
   trackpad drag work, with auto-advance layered on top. */
.site .banner-track{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch;height:100%}
.site .banner-track::-webkit-scrollbar{display:none}
.site .banner-slide{flex:0 0 100%;scroll-snap-align:center;height:100%}
.site .banner-slide img{width:100%;height:100%;object-fit:cover;display:block}
.site .banner-nav{position:absolute;top:50%;transform:translateY(-50%);width:38px;height:38px;border-radius:999px;background:rgba(0,0,0,.38);color:#fff;display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:800;border:1px solid rgba(255,255,255,.35);cursor:pointer;opacity:0;transition:opacity .2s ease,background .2s ease;z-index:2}
.site .banner-box:hover .banner-nav,.site .banner-nav:focus-visible{opacity:1}
.site .banner-nav:hover{background:rgba(0,0,0,.6)}
.site .banner-nav.prev{left:10px}
.site .banner-nav.next{right:10px}
.site .banner-dots i{cursor:pointer;transition:width .2s ease,background .2s ease}

/* Generic horizontal scroller used by the gallery and review rails. */
.site .rail-wrap{position:relative}
.site .rail{display:grid;grid-auto-flow:column;gap:16px;overflow-x:auto;scroll-snap-type:x proximity;scroll-behavior:smooth;padding:4px 2px 14px;scrollbar-width:none;-webkit-overflow-scrolling:touch}
.site .rail::-webkit-scrollbar{display:none}
.site .rail > *{scroll-snap-align:start}
/* Two stacked rows that scroll sideways together. */
.site .rail-2row{grid-template-rows:repeat(2,1fr);grid-auto-columns:260px}
.site .rail-1row{grid-template-rows:1fr;grid-auto-columns:330px}
.site .rail-nav{position:absolute;top:50%;transform:translateY(-50%);width:42px;height:42px;border-radius:999px;background:#fff;border:1px solid var(--line);box-shadow:0 8px 22px rgba(20,22,40,.14);display:flex;align-items:center;justify-content:center;font-size:19px;font-weight:800;color:var(--brand);cursor:pointer;z-index:2;transition:opacity .2s ease,transform .15s ease}
.site .rail-nav:hover{transform:translateY(-50%) scale(1.06)}
.site .rail-nav[disabled]{opacity:0;pointer-events:none}
.site .rail-nav.prev{left:-18px}
.site .rail-nav.next{right:-18px}
.site .sec-head{display:flex;align-items:flex-end;justify-content:space-between;gap:20px;margin-bottom:22px;flex-wrap:wrap}
/* Let the heading block share the row so 'View all' stays beside it. */
.site .sec-head > :first-child{flex:1 1 320px;min-width:0}
.site .sec-head .lead{margin-bottom:0}
.site .viewall{color:var(--brand);font-weight:800;font-size:14.5px;text-decoration:none;white-space:nowrap;display:inline-flex;align-items:center;gap:6px}
.site .viewall:hover{text-decoration:underline}

/* Gallery */
.site .gal-item{position:relative;border-radius:16px;overflow:hidden;background:#fff;border:1px solid var(--line);aspect-ratio:4/3;cursor:zoom-in}
/* contain, not cover: a fixed 4:3 crop chopped the top and bottom off any
   portrait photo. The white plate behind fills the leftover space, matching
   the page rather than boxing every photo in black. */
.site .gal-item img,.site .gal-item video{width:100%;height:100%;object-fit:contain;display:block;transition:transform .35s ease;background:#fff}
.site .gal-item:hover img,.site .gal-item:hover video{transform:scale(1.04)}
/* Play badge marks a clip without autoplaying a rail full of video. */
.site .gal-play{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:46px;height:46px;border-radius:999px;background:rgba(0,0,0,.55);border:1.5px solid rgba(255,255,255,.85);color:#fff;display:flex;align-items:center;justify-content:center;font-size:15px;padding-left:3px;pointer-events:none;transition:transform .2s ease,background .2s ease}
.site .gal-item:hover .gal-play{background:var(--brand);transform:translate(-50%,-50%) scale(1.08)}
/* Plates are white now, so a dark scrim read as a grey smudge under
   letterboxed photos. Light scrim + dark text works on both. */
.site .gal-cap{position:absolute;left:0;right:0;bottom:0;padding:26px 12px 10px;color:var(--ink);font-size:12.5px;font-weight:700;background:linear-gradient(transparent,rgba(255,255,255,.72) 38%,rgba(255,255,255,.96))}
.site .gal-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px}

/* Full-screen viewer */
.site .lb{position:fixed;inset:0;z-index:200;background:rgba(10,7,8,.94);display:flex;align-items:center;justify-content:center;padding:64px 20px 72px;animation:lb-in .18s ease}
@keyframes lb-in{from{opacity:0}to{opacity:1}}
.site .lb-stage{margin:0;display:flex;flex-direction:column;align-items:center;gap:14px;max-width:min(1200px,94vw)}
.site .lb-stage img,.site .lb-stage video{max-width:min(1200px,94vw);max-height:78vh;width:auto;height:auto;object-fit:contain;border-radius:10px;display:block;background:#fff}
.site .lb-stage figcaption{color:rgba(255,255,255,.88);font-size:14.5px;text-align:center;max-width:720px}
.site .lb-close{position:absolute;top:16px;right:18px;width:46px;height:46px;border-radius:999px;background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.34);color:#fff;font-size:20px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .18s ease,transform .18s ease;z-index:2}
.site .lb-close:hover{background:var(--brand);border-color:var(--brand);transform:scale(1.06)}
.site .lb-count{position:absolute;top:26px;left:50%;transform:translateX(-50%);color:rgba(255,255,255,.65);font-size:13px;font-weight:600;letter-spacing:.4px}
.site .lb-nav{position:absolute;top:50%;transform:translateY(-50%);width:52px;height:52px;border-radius:999px;background:rgba(255,255,255,.12);border:1px solid rgba(255,255,255,.3);color:#fff;font-size:28px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;transition:background .18s ease;z-index:2}
.site .lb-nav:hover{background:var(--brand);border-color:var(--brand)}
.site .lb-nav.prev{left:20px}
.site .lb-nav.next{right:20px}
@media (max-width:900px){
  .site .lb{padding:60px 12px 76px}
  .site .lb-nav{width:44px;height:44px;font-size:24px}
  .site .lb-nav.prev{left:8px}
  .site .lb-nav.next{right:8px}
  .site .lb-stage img,.site .lb-stage video{max-height:70vh}
}

/* Reviews */
.site .rv-head{display:flex;align-items:center;gap:16px;flex-wrap:wrap;margin-bottom:22px}
.site .rv-score{display:flex;align-items:baseline;gap:8px}
.site .rv-score b{font-size:42px;font-weight:900;letter-spacing:-1px;color:var(--ink);line-height:1}
.site .rv-stars{color:#F5A623;font-size:16px;letter-spacing:2px}
.site .rv-meta{color:var(--muted);font-size:14px}
.site .rv-card{background:#fff;border:1px solid var(--line);border-radius:18px;padding:22px;display:flex;flex-direction:column;gap:10px;height:100%}
.site .rv-top{display:flex;align-items:center;gap:12px}
.site .rv-av{width:42px;height:42px;border-radius:999px;background:var(--brand-soft);color:var(--brand);display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;flex:none;overflow:hidden}
.site .rv-av img{width:100%;height:100%;object-fit:cover}
.site .rv-name{font-weight:800;font-size:14.5px}
.site .rv-date{color:var(--faint);font-size:12px}
.site .rv-text{color:var(--muted);font-size:14px;line-height:1.65;margin:0;display:-webkit-box;-webkit-line-clamp:6;-webkit-box-orient:vertical;overflow:hidden}
.site .rv-g{margin-left:auto;font-size:11px;font-weight:800;color:var(--faint);letter-spacing:.4px}

/* Sections */
.site .sec{padding:84px 0}
.site .sec-alt{background:var(--bg-alt)}
.site .eyebrow{color:var(--brand);font-weight:800;font-size:13px;letter-spacing:2px;text-transform:uppercase;margin:0 0 10px}
.site .sec h2{font-size:38px;font-weight:900;margin:0 0 12px;letter-spacing:-.4px}
.site .sec .lead{color:var(--muted);font-size:16.5px;max-width:620px;margin:0 0 44px;line-height:1.6}
.site .center{text-align:center}
.site .center .lead{margin-left:auto;margin-right:auto}

.site .grid{display:grid;gap:20px}
.site .g3{grid-template-columns:repeat(3,1fr)}
.site .g4{grid-template-columns:repeat(4,1fr)}
.site .card{background:#fff;border:1px solid var(--line);border-radius:18px;padding:26px;transition:transform .2s ease, box-shadow .2s ease;height:100%;box-shadow:none}
.site .card:hover{transform:translateY(-6px);box-shadow:0 18px 44px rgba(20,22,40,.10)}
.site .card .ico{width:56px;height:56px;border-radius:16px;background:var(--brand-soft);display:flex;align-items:center;justify-content:center;font-size:26px;margin-bottom:16px}
.site .card h3{font-size:17.5px;margin:0 0 8px;font-weight:800}
.site .card p{color:var(--muted);font-size:14px;line-height:1.6;margin:0}

/* Steps */
.site .step{display:flex;gap:18px;align-items:flex-start}
.site .step-n{flex:0 0 44px;height:44px;border-radius:999px;background:var(--brand);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:900;box-shadow:0 8px 20px rgba(193,18,31,.3)}
.site .step h3{margin:6px 0 6px;font-size:17px;font-weight:800}
.site .step p{color:var(--muted);font-size:14px;line-height:1.6;margin:0}

/* Stats */
.site .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:20px;text-align:center}
.site .stat b{display:block;font-size:40px;font-weight:900;color:var(--brand);letter-spacing:-1px}
.site .stat span{color:var(--muted);font-size:14px;font-weight:600}

/* Download CTA */
.site .cta{background:linear-gradient(135deg,var(--brand-dark),var(--brand));color:#fff;border-radius:28px;padding:58px 40px;text-align:center;position:relative;overflow:hidden}
.site .cta h2{font-size:36px;margin:0 0 12px;font-weight:900}
.site .cta p{opacity:.9;font-size:16px;margin:0 0 26px}
.site .cta .btn-primary{background:#fff;color:var(--brand);box-shadow:0 10px 30px rgba(0,0,0,.22)}
.site .cta .cta-row{display:flex;gap:14px;justify-content:center;flex-wrap:wrap}
.site .cta .btn-ghost{background:transparent;color:#fff;border-color:rgba(255,255,255,.6)}

/* FAQ */
.site .faq{border:1px solid var(--line);border-radius:14px;background:#fff;margin-bottom:12px;overflow:hidden}
.site .faq summary{cursor:pointer;padding:18px 20px;font-weight:700;font-size:15px;list-style:none;display:flex;justify-content:space-between;align-items:center;gap:16px}
.site .faq summary::-webkit-details-marker{display:none}
.site .faq summary:after{content:'+';color:var(--brand);font-size:22px;font-weight:800}
.site .faq[open] summary:after{content:'–'}
.site .faq p{margin:0;padding:0 20px 18px;color:var(--muted);font-size:14.5px;line-height:1.65}

/* Registrations / certifications band */
.site .reg-band{padding:34px 0 6px;background:var(--bg)}
.site .reg-band__title{text-align:center;color:var(--faint);font-size:12px;font-weight:800;letter-spacing:2px;text-transform:uppercase;margin:0 0 18px}
.site .reg-band__row{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:30px 44px}
.site .reg-band__row img{height:60px;width:auto;object-fit:contain;filter:grayscale(1);opacity:.7;transition:filter .2s ease,opacity .2s ease}
.site .reg-band__row img:hover{filter:none;opacity:1}

/* Floating WhatsApp button */
.site .wa-fab{position:fixed;right:20px;bottom:22px;z-index:120;display:inline-flex;align-items:center;gap:0;height:58px;padding:0;width:58px;border-radius:999px;background:#25D366;box-shadow:0 8px 24px rgba(37,211,102,.45);overflow:hidden;transition:width .25s ease,padding .25s ease}
.site .wa-fab svg{flex:0 0 58px;width:58px;height:58px;padding:14px}
.site .wa-fab__label{color:#fff;font-weight:800;font-size:14.5px;white-space:nowrap;opacity:0;max-width:0;transition:opacity .2s ease,max-width .25s ease}
.site .wa-fab:hover{width:212px;padding-right:20px}
.site .wa-fab:hover .wa-fab__label{opacity:1;max-width:150px}
.site .wa-fab::before{content:'';position:absolute;inset:0;border-radius:999px;background:#25D366;animation:wa-pulse 2.4s ease-out infinite;z-index:-1}
@keyframes wa-pulse{0%{transform:scale(1);opacity:.55}70%{transform:scale(1.5);opacity:0}100%{opacity:0}}
@media (max-width:560px){
  .site .wa-fab{right:16px;bottom:16px}
  .site .reg-band__row{gap:22px 30px}
  .site .reg-band__row img{height:44px}
}
@media (prefers-reduced-motion: reduce){ .site .wa-fab::before{animation:none} }

/* Footer *//* Footer */
.site .foot{background:var(--ink);color:#fff;padding:60px 0 26px}
.site .foot-grid{display:grid;grid-template-columns:1.4fr 1fr 1fr 1.2fr;gap:36px}
.site .foot h4{font-size:14px;font-weight:800;margin:0 0 16px;letter-spacing:.4px}
.site .foot a,.site .foot p{color:rgba(255,255,255,.66);text-decoration:none;font-size:14px;line-height:2;display:block;margin:0}
.site .foot a:hover{color:#fff}
.site .foot-logo{height:40px;width:auto;background:#fff;border-radius:10px;padding:6px 10px;margin-bottom:14px}
.site .socials{display:flex;gap:10px;margin-top:14px}
.site .socials a{width:40px;height:40px;border-radius:999px;background:rgba(255,255,255,.1);display:flex;align-items:center;justify-content:center;color:#fff}
.site .socials a:hover{background:var(--brand)}
.site .foot-bot{border-top:1px solid rgba(255,255,255,.12);margin-top:40px;padding-top:22px;display:flex;justify-content:space-between;gap:14px;flex-wrap:wrap;color:rgba(255,255,255,.5);font-size:13px}

/* Doc pages (privacy / delete) */
.site .doc{padding:56px 20px 80px;max-width:820px}
.site .doc h1{font-size:38px;font-weight:900;margin:0 0 8px}
.site .doc .upd{color:var(--faint);font-size:13.5px;margin-bottom:30px}
.site .doc p,.site .doc li{color:var(--muted);font-size:15px;line-height:1.8}
.site .doc h2{font-size:20px;margin:32px 0 10px;color:var(--ink);font-weight:800}
.site .doc .pre{white-space:pre-wrap}

/* Mobile */
@media (max-width: 900px){
  .site .hero-in{grid-template-columns:1fr;padding:56px 20px 64px;gap:32px}
  .site .hero h1{font-size:36px}
  .site .hero p{font-size:16px}
  .site .nav-links{display:none}
  .site .sec{padding:60px 0}
  .site .sec h2{font-size:28px}
  .site .g3,.site .g4{grid-template-columns:1fr 1fr}
  .site .stats{grid-template-columns:1fr 1fr;gap:26px}
  .site .stat b{font-size:30px}
  .site .foot-grid{grid-template-columns:1fr 1fr}
  .site .cta{padding:42px 22px;border-radius:20px}
  .site .cta h2{font-size:26px}
  .site .doc h1{font-size:28px}
  .site .rail-2row{grid-auto-columns:200px}
  .site .rail-1row{grid-auto-columns:280px}
  .site .rail-nav{display:none}
  .site .gal-grid{grid-template-columns:repeat(2,1fr)}
  .site .rv-score b{font-size:34px}
}
@media (max-width: 560px){
  .site .g3,.site .g4{grid-template-columns:1fr}
  .site .foot-grid{grid-template-columns:1fr}
  .site .hero h1{font-size:30px}
  .site .hero-cta .btn{width:100%}
  .site .nav-logo{height:30px}
  .site .nav-actions .btn{padding:10px 15px;font-size:13.5px}
  .site .rail-2row{grid-auto-columns:160px}
  .site .rail-1row{grid-auto-columns:250px}
}
@media (prefers-reduced-motion: reduce){
  .site .reveal{opacity:1;transform:none;transition:none}
  .site .hero-anim,.site .hero-anim-2{animation:none}
}
`;

export default siteCss;
